import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Box, Grid, Paper, Typography, Button,
    Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, LinearProgress,
    Card, CardContent, Divider, Alert, TextField, Slider, IconButton, Tooltip, CircularProgress,
    Collapse, TableSortLabel, Checkbox, FormControlLabel, Autocomplete,
    Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import {
    Analytics, CompareArrows, AutoGraph, Add, Delete,
    PlayArrow, OpenInNew, EmojiEvents, GridOn, ExpandMore, ExpandLess,
    Close, LocalGasStation, BatteryChargingFull, WbSunny, Speed, AttachMoney
} from '@mui/icons-material';
import { useAuth, vortxColors, useEquipmentDatabase, calculateCapexWithItc, monthlyPerformanceService, touDataToSolverFormat, API_BASE_URL, vortxTypography } from '@vortx/shared-ui';
import { FinancialCalculator } from '../FinancialAnalysis';
import ApprovedTariffSelector from './scenario/ApprovedTariffSelector';

/**
 * ScenarioAnalysis Component
 *
 * Enhanced with DER Matrix Builder for parallel scenario optimization.
 * Features:
 * - Matrix builder to generate PV × Battery combinations
 * - Parallel optimization of all variants
 * - Results ranked by NPV
 * - Save-as-new-scenario functionality
 * - Budget-based CapEx calculations using custom rates
 */
export default function ScenarioAnalysis({
    projectId,
    currentScenarioId,
    currentScenarioData,
    budgetRates,             // Custom capex rates from Budget tab
    budget,                  // Full budget object with opexCalculations
    commercialAssumptions,   // Commercial assumptions with escalation rates
    itcSettings,             // ITC settings: { inputs, includeItc }
    mainLoadProfile,         // Full 8760 load profile (backup if missing from scenario)
    monthlyPerformanceData,  // Monthly billing data for financial calculations (main scenario)
    gasTariffData,           // Gas tariff data for tiered cost calculation
    electricTariffData,      // Electric tariff data for variant monthly performance calculations
    adminCapexRates          // Admin-managed CapEx rates for Matrix Builder (system-wide defaults)
}) {
    const { currentUser } = useAuth();
    // Scenario comparison feature removed - now shows only current scenario
    const [loading, setLoading] = useState(true);

    // Check if admin rates are available for Matrix Builder
    const hasAdminRates = adminCapexRates?.hasValidRates;

    // Fetch equipment from database for matrix builder
    const { data: solarEquipment = [] } = useEquipmentDatabase('solar');
    const { data: batteryEquipment = [] } = useEquipmentDatabase('bess');
    const { data: generatorEquipment = [] } = useEquipmentDatabase('generator');

    // Matrix Builder State - Arrays of values (chip-based)
    const [matrixExpanded, setMatrixExpanded] = useState(true);

    // Track if user has explicitly cleared variants (prevents reload from backend)
    const [variantsCleared, setVariantsCleared] = useState(false);

    // Enable checkboxes for each asset type
    const [pvEnabled, setPvEnabled] = useState(false);
    const [batteryEnabled, setBatteryEnabled] = useState(false);
    const [generatorEnabled, setGeneratorEnabled] = useState(false);

    // Value arrays (start empty - user adds values when enabled)
    const [pvValues, setPvValues] = useState([]);
    // Battery stores objects: {kW, duration} where duration is 2 or 4 hours
    const [batteryConfigs, setBatteryConfigs] = useState([]);
    const [generatorValues, setGeneratorValues] = useState([]);

    // Input field states for adding new values
    const [pvInput, setPvInput] = useState('');
    const [batteryInput, setBatteryInput] = useState('');
    const [batteryDuration, setBatteryDuration] = useState(4); // 2 or 4 hours
    const [generatorInput, setGeneratorInput] = useState('');

    // Generated Variants
    const [variants, setVariants] = useState([]);

    // Run State
    const [isRunning, setIsRunning] = useState(false);
    const [runProgress, setRunProgress] = useState(0);
    const [variantResults, setVariantResults] = useState([]);
    const [sortOrder, setSortOrder] = useState('desc'); // NPV sort order

    // Detail modal state
    const [selectedVariant, setSelectedVariant] = useState(null);
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);

    // Tariff comparison state
    const [tariffComparisonEnabled, setTariffComparisonEnabled] = useState(false);
    const [comparisonTariffs, setComparisonTariffs] = useState([]); // Array of tariff objects

    const updateVariantStatus = useCallback((variantId, status) => {
        setVariants((prev) => prev.map((variant) => (
            variant.id === variantId ? { ...variant, status } : variant
        )));
    }, []);

    /**
     * Build ElectricTariff payload from tariff data object
     * Handles different tariff formats (structured, blended, native 8760)
     */
    const buildTariffPayload = useCallback((tariffData) => {
        if (!tariffData) return null;

        const rd = tariffData.rate_data || tariffData;

        // Native 8760 format
        if (tariffData.rate_mode === 'native_8760' && rd.energy_charges) {
            return {
                custom_electricity_rate_series_kw: rd.energy_charges,
                _vortx_native_8760: {
                    energy_charges: rd.energy_charges,
                    demand_charges: rd.demand_charges,
                    monthly_fixed_charges: rd.monthly_fixed_charges
                },
                urdb_utility_name: tariffData.utility_name || '',
                urdb_rate_name: tariffData.name || tariffData.rate_schedule_name || '',
            };
        }

        // Structured rate format (has rate structures for solver)
        if (rd.urdb_rate_data || rd.energyratestructure) {
            const solverResponse = rd.urdb_rate_data || {
                energyratestructure: rd.energyratestructure,
                demandratestructure: rd.demandratestructure,
                flatdemandstructure: rd.flatdemandstructure,
                energyweekdayschedule: rd.energyweekdayschedule,
                energyweekendschedule: rd.energyweekendschedule,
                demandweekdayschedule: rd.demandweekdayschedule,
                demandweekendschedule: rd.demandweekendschedule,
                flatdemandmonths: rd.flatdemandmonths,
                fixedmonthlycharge: rd.fixedmonthlycharge || rd.fixedMonthlyCharge || 0,
            };
            return {
                urdb_response: solverResponse,
                urdb_utility_name: tariffData.utility_name || '',
                urdb_rate_name: tariffData.name || tariffData.rate_schedule_name || '',
            };
        }

        // Blended rate format
        if (rd.blendedEnergyRate !== undefined) {
            return {
                blended_annual_energy_rate: Number(rd.blendedEnergyRate),
                blended_annual_demand_rate: Number(rd.blendedDemandRate || 0),
                fixedmonthlycharge: Number(rd.fixedmonthlycharge || rd.fixedMonthlyCharge || 0),
            };
        }

        // TOU Builder format (has periods with schedule) - use shared utility function
        if (rd.touData?.periods || rd.periods) {
            const touData = rd.touData || rd;
            console.log('[buildTariffPayload] Converting TOU Builder format to solver format');
            const solverFormat = touDataToSolverFormat(touData);
            if (solverFormat) {
                return {
                    urdb_response: solverFormat,
                    urdb_utility_name: tariffData.utility_name || '',
                    urdb_rate_name: tariffData.name || tariffData.rate_schedule_name || '',
                };
            }
        }

        // Log unrecognized format for debugging
        console.warn('[buildTariffPayload] Unrecognized tariff format, returning null:', {
            hasRateMode: !!tariffData.rate_mode,
            rateMode: tariffData.rate_mode,
            hasRateData: !!tariffData.rate_data,
            rateDataKeys: tariffData.rate_data ? Object.keys(tariffData.rate_data) : [],
            tariffKeys: Object.keys(tariffData)
        });
        return null;
    }, []);

    const buildVariantScenario = useCallback((variant, baseInputs, baseConfig, adminRates) => {
        const scenario = { ...baseInputs };

        // Calculate combined rates from admin-managed defaults
        const batteryCostPerKwh = (adminRates?.batteryEquipmentRate || 0) + (adminRates?.batteryInstallRate || 0);
        const generatorCostPerKw = (adminRates?.generatorEquipmentRate || 0) + (adminRates?.generatorInstallRate || 0);

        if (variant.pvKw > 0) {
            scenario.PV = {
                ...(baseConfig.PV || {}),
                min_kw: variant.pvKw,
                max_kw: variant.pvKw,
                // Explicitly set costs from admin-managed defaults
                installed_cost_per_kw: adminRates?.pvTurnkeyRate,
                federal_itc_fraction: 0.30,
            };
        } else if (baseConfig.PV) {
            scenario.PV = {
                ...baseConfig.PV,
                min_kw: 0,
                max_kw: 0,
                installed_cost_per_kw: adminRates?.pvTurnkeyRate,
            };
        }

        if (variant.batteryKwh > 0) {
            // Strip max_cycles_per_year from top level — it lives in _vortx_battery_config now
            const { max_cycles_per_year: _mcpy, ...baseStorage } = baseConfig.ElectricStorage || {};
            scenario.ElectricStorage = {
                ...baseStorage,
                min_kw: variant.batteryKw,
                max_kw: variant.batteryKw,
                min_kwh: variant.batteryKwh,
                max_kwh: variant.batteryKwh,
                // Explicitly set costs from admin-managed defaults
                installed_cost_per_kw: adminRates?.batteryPowerRate,
                installed_cost_per_kwh: batteryCostPerKwh,
                total_itc_fraction: 0.30,
            };
        } else if (baseConfig.ElectricStorage) {
            const { max_cycles_per_year: _mcpy2, ...baseStorage2 } = baseConfig.ElectricStorage;
            scenario.ElectricStorage = {
                ...baseStorage2,
                min_kw: 0,
                max_kw: 0,
                min_kwh: 0,
                max_kwh: 0,
                installed_cost_per_kw: adminRates?.batteryPowerRate,
                installed_cost_per_kwh: batteryCostPerKwh,
            };
        }

        if (variant.generatorKw > 0) {
            // Check if base scenario uses CHP (natural gas) or Generator (diesel)
            // Send the SAME object type that main scenario sent - backend will convert CHP→Generator
            if (baseConfig.CHP) {
                // Natural gas - send CHP object (backend converts to Generator)
                scenario.CHP = {
                    ...baseConfig.CHP,
                    min_kw: variant.generatorKw,
                    max_kw: variant.generatorKw,
                    // Ensure critical params have defaults if missing
                    fuel_cost_per_mmbtu: baseConfig.CHP.fuel_cost_per_mmbtu ?? 6.0,
                    installed_cost_per_kw: generatorCostPerKw,
                    _vortx_uptime_pct: baseConfig.CHP._vortx_uptime_pct ?? 96.5,
                };
                console.log(`[buildVariantScenario] Created CHP object for ${variant.generatorKw}kW (natural gas) with cost $${generatorCostPerKw}/kW`);
            } else if (baseConfig.Generator) {
                // Diesel - send Generator object
                scenario.Generator = {
                    ...baseConfig.Generator,
                    min_kw: variant.generatorKw,
                    max_kw: variant.generatorKw,
                    // Ensure critical params have defaults if missing
                    fuel_cost_per_gallon: baseConfig.Generator.fuel_cost_per_gallon ?? 3.0,
                    installed_cost_per_kw: generatorCostPerKw,
                    _vortx_uptime_pct: baseConfig.Generator._vortx_uptime_pct ?? 1.0,
                };
                console.log(`[buildVariantScenario] Created Generator object for ${variant.generatorKw}kW (diesel) with cost $${generatorCostPerKw}/kW`);
            } else {
                // No base generator config - create new Generator with defaults
                scenario.Generator = {
                    min_kw: variant.generatorKw,
                    max_kw: variant.generatorKw,
                    fuel_cost_per_gallon: 3.0,
                    installed_cost_per_kw: generatorCostPerKw,
                };
                console.log(`[buildVariantScenario] Created new Generator object for ${variant.generatorKw}kW with cost $${generatorCostPerKw}/kW`);
            }
        } else {
            // No generator in variant - zero out existing config
            if (baseConfig.CHP) {
                scenario.CHP = { ...baseConfig.CHP, min_kw: 0, max_kw: 0, installed_cost_per_kw: generatorCostPerKw };
            }
            if (baseConfig.Generator) {
                scenario.Generator = { ...baseConfig.Generator, min_kw: 0, max_kw: 0, installed_cost_per_kw: generatorCostPerKw };
            }
        }

        // Override ElectricTariff if variant has specific tariff
        if (variant.tariff && !variant.tariff._isBase) {
            const tariffPayload = buildTariffPayload(variant.tariff);
            if (tariffPayload) {
                scenario.ElectricTariff = tariffPayload;
                console.log(`[Matrix] Variant using tariff: ${variant.tariffLabel}`, tariffPayload);
            } else {
                console.warn(`[Matrix] WARNING: buildTariffPayload returned null for tariff: ${variant.tariffLabel}`, {
                    tariffId: variant.tariff?.id,
                    tariffName: variant.tariff?.name,
                    hasRateData: !!variant.tariff?.rate_data,
                    rateDataKeys: variant.tariff?.rate_data ? Object.keys(variant.tariff.rate_data) : [],
                    willUseBaseTariff: true
                });
            }
        }
        // If _isBase or no tariff, keep the ElectricTariff already in baseInputs

        return scenario;
    }, [buildTariffPayload]);

    /**
     * Calculate CapEx using shared budget calculation utility
     * This ensures variants use the SAME calculation method as the main scenario
     */
    const calculateBudgetCapex = useCallback((pvKw, batteryKwh, batteryKw, generatorKw) => {
        console.log('💰 [calculateBudgetCapex] Called with:', {
            pvKw,
            batteryKwh,
            batteryKw,
            generatorKw,
            hasBudgetRates: !!budgetRates,
            hasItcSettings: !!itcSettings
        });

        if (!budgetRates) {
            console.error('[calculateBudgetCapex] No budgetRates provided');
            return null;
        }

        // Use shared calculation utility - same method as main scenario
        const result = calculateCapexWithItc(
            { pvKw, batteryKw, batteryKwh, generatorKw },
            budgetRates,
            itcSettings
        );

        console.log('💰 [calculateBudgetCapex] Result from shared utility:', {
            totalCapex: result.totalCapex,
            capexAfterIncentives: result.capexAfterIncentives,
            totalItc: result.totalItc,
            itcApplied: result.itcApplied,
            breakdown: result.breakdown
        });

        return {
            totalCapex: result.totalCapex,
            capexAfterIncentives: result.capexAfterIncentives,
            totalItc: result.totalItc,
            itcApplied: result.itcApplied
        };
    }, [budgetRates, itcSettings]);

    /**
     * Summarize variant results using FinancialCalculator
     * @param {Object} variant - The variant configuration
     * @param {Object} data - Solver results for this variant
     * @param {Object} variantMonthlyData - Variant-specific monthly performance data (optional)
     *                                      If not provided, falls back to main scenario data
     */
    const summarizeVariantResults = useCallback((variant, data, variantMonthlyData = null) => {
        // Each variant must use its own monthly data — no borrowing from base scenario
        const effectiveMonthlyData = variantMonthlyData;
        if (!effectiveMonthlyData || !effectiveMonthlyData.bau_monthly_costs?.length) {
            console.error(`❌ [${variant.name}] Variant monthly data missing — BAU/Project costs will be zero`, {
                monthlyDataNull: effectiveMonthlyData === null,
                monthlyDataKeys: effectiveMonthlyData ? Object.keys(effectiveMonthlyData) : null,
                bauLength: effectiveMonthlyData?.bau_monthly_costs?.length,
                tariffSource: effectiveMonthlyData?.tariff_source,
                tariffWarning: effectiveMonthlyData?.tariff_warning
            });
        }

        // Get system sizes from solver output
        const outputs = data?.outputs || {};
        const pvKw = outputs?.PV?.size_kw ?? variant.pvKw ?? 0;
        const batteryKwh = outputs?.ElectricStorage?.size_kwh ?? variant.batteryKwh ?? 0;
        const batteryKw = outputs?.ElectricStorage?.size_kw ?? variant.batteryKw ?? 0;
        const generatorKw = outputs?.Generator?.size_kw ?? outputs?.CHP?.size_kw ?? variant.generatorKw ?? 0;

        console.log(`📊 [${variant.name}] System sizes:`, { pvKw, batteryKwh, batteryKw, generatorKw });

        // Calculate CapEx using shared utility
        const budgetCapex = calculateBudgetCapex(pvKw, batteryKwh, batteryKw, generatorKw);
        if (!budgetCapex || budgetCapex.totalCapex == null) {
            console.error(`[${variant.name}] Cannot calculate - budgetCapex:`, budgetCapex);
            return {
                name: variant.name,
                pvKw,
                batteryKwh,
                batteryKw,
                generatorKw,
                npv: null,
                capex: null,
                payback: null,
                error: 'Budget rates not available'
            };
        }

        console.log(`📊 [${variant.name}] Budget CapEx calculated:`, {
            totalCapex: budgetCapex.totalCapex,
            capexAfterIncentives: budgetCapex.capexAfterIncentives,
            totalItc: budgetCapex.totalItc
        });

        // Use FinancialCalculator - SAME METHOD AS MAIN SCENARIO
        console.log(`📊 [${variant.name}] Creating FinancialCalculator with:`, {
            dataKeys: Object.keys(data || {}),
            outputsKeys: Object.keys(data?.outputs || {}),
            monthlyDataStructure: effectiveMonthlyData ? {
                bauMonths: effectiveMonthlyData.bau_monthly_costs?.length,
                projectMonths: effectiveMonthlyData.project_monthly_costs?.length,
                isVariantSpecific: !!variantMonthlyData
            } : null,
            capex: budgetCapex.totalCapex,
            capexAfterItc: budgetCapex.capexAfterIncentives,
            hasOpex: !!budget?.opexCalculations,
            itc: budgetCapex.totalItc,
            hasEscalation: !!commercialAssumptions?.calculatorInputs?.escalationRates
        });

        // CRITICAL FIX: Pass totalCapex (BEFORE ITC), not capexAfterIncentives (AFTER ITC)
        // FinancialCalculator subtracts ITC internally, so passing capexAfterIncentives would double-subtract

        const calculator = new FinancialCalculator(
            data,                                                // Solver results (outputs, status, etc.)
            {},                                                  // Empty object (legacy param)
            effectiveMonthlyData,                                // Variant-specific or main scenario monthly data
            budgetCapex.totalCapex,                              // Capital cost BEFORE ITC (FinancialCalculator subtracts ITC)
            budget?.opexCalculations,                            // OpEx from Budget tab
            budgetCapex.totalItc,                                // ITC amount to subtract
            commercialAssumptions?.calculatorInputs?.escalationRates, // Escalation rates from Commercial tab
            gasTariffData,                                       // Gas tariff for tiered costs
            commercialAssumptions?.calculatorInputs              // Full commercial assumptions
        );

        const analysis = calculator.getComprehensiveAnalysis();

        // NPV and payback are inside the summary object
        const calculatedNpv = analysis?.summary?.npv;
        const calculatedPayback = analysis?.summary?.paybackPeriod;
        // Year one savings = BAU cost - Project cost
        const yearOneBauCost = analysis?.summary?.bauYearOneUtilityCost || 0;
        const yearOneProjectCost = analysis?.projectCashflows?.[0]?.projectUtilityCost || 0;
        const calculatedSavings = yearOneBauCost - yearOneProjectCost;

        if (calculatedNpv === null || calculatedNpv === undefined || Number.isNaN(calculatedNpv)) {
            console.error(`❌ [${variant.name}] NPV calculation failed - check monthly data availability`);
        }

        console.log(`📊 [${variant.name}] FinancialCalculator Result:`, {
            npv: calculatedNpv,
            payback: calculatedPayback,
            capex: budgetCapex.capexAfterIncentives,
            yearOneSavings: calculatedSavings
        });

        // Extract detailed metrics from monthly performance data for detail view
        const assetPerformance = variantMonthlyData?.asset_performance || [];
        console.log(`📊 [${variant.name}] ASSET PERFORMANCE DEBUG:`, {
            hasMonthlyData: !!variantMonthlyData,
            assetPerformanceLength: assetPerformance.length,
            assetPerformanceKeys: assetPerformance[0] ? Object.keys(assetPerformance[0]) : [],
            sampleMonth: assetPerformance[0] || null,
            generatorDataSample: assetPerformance[0] ? {
                generator_to_load_kwh: assetPerformance[0].generator_to_load_kwh,
                generator_runtime_hours: assetPerformance[0].generator_runtime_hours,
                load_kwh: assetPerformance[0].load_kwh
            } : null
        });
        const annualMetrics = assetPerformance.reduce((acc, month) => ({
            totalLoad: (acc.totalLoad || 0) + (month.load_kwh || 0),
            gridImport: (acc.gridImport || 0) + (month.grid_to_load_kwh || 0),
            solarProduction: (acc.solarProduction || 0) + (month.pv_production_kwh || month.pv_to_load_kwh || 0),
            batteryThroughput: (acc.batteryThroughput || 0) + (month.storage_to_load_kwh || 0),
            generatorOutput: (acc.generatorOutput || 0) + (month.generator_to_load_kwh || 0),
            generatorRuntimeHours: (acc.generatorRuntimeHours || 0) + (month.generator_runtime_hours || 0),
            fuelUsedMmbtu: (acc.fuelUsedMmbtu || 0) + (month.generator_fuel_used_mmbtu || 0),
            batteryCycles: (acc.batteryCycles || 0) + (month.battery_cycles || 0),
            peakLoadKw: Math.max(acc.peakLoadKw || 0, month.peak_load_kw || 0),
            postDerPeakKw: Math.max(acc.postDerPeakKw || 0, month.post_der_peak_kw || 0),
        }), {});

        // Annual costs from FinancialCalculator (single source of truth)
        const annualBauCost = yearOneBauCost;
        const annualProjectCost = yearOneProjectCost;

        // Extract emissions data from solver outputs
        const site = outputs?.Site || {};
        const emissionsBaseline = site.lifecycle_emissions_tonnes_CO2_bau ??
                                   site.annual_emissions_tonnes_CO2_bau ??
                                   outputs?.lifecycle_emissions_tonnes_CO2_bau ?? null;
        const emissionsProject = site.lifecycle_emissions_tonnes_CO2 ??
                                  site.annual_emissions_tonnes_CO2 ??
                                  outputs?.lifecycle_emissions_tonnes_CO2 ?? null;
        const emissionsReduction = (emissionsBaseline !== null && emissionsProject !== null)
            ? emissionsBaseline - emissionsProject : null;
        const emissionsReductionPct = (emissionsBaseline !== null && emissionsProject !== null && emissionsBaseline > 0)
            ? ((emissionsBaseline - emissionsProject) / emissionsBaseline) * 100 : null;

        return {
            name: variant.name,
            pvKw,
            batteryKwh,
            batteryKw,
            generatorKw,
            npv: calculatedNpv,
            capex: budgetCapex.capexAfterIncentives,
            payback: calculatedPayback,
            yearOneSavings: calculatedSavings,
            itcApplied: budgetCapex.itcApplied,
            totalItc: budgetCapex.totalItc,
            runUuid: data?.run_uuid,
            usingBudgetRates: true,
            // Detailed metrics for detail view
            annualMetrics,
            annualBauCost,
            annualProjectCost,
            annualSavings: annualBauCost - annualProjectCost,
            monthlyPerformance: assetPerformance,
            opexAnnual: budget?.opexCalculations?.totalAnnualOM || 0,
            // Emissions data
            emissionsBaseline,
            emissionsProject,
            emissionsReduction,
            emissionsReductionPct,
            // Tariff info for display
            tariffLabel: variant.tariffLabel || 'Base Tariff',
            tariffId: variant.tariff?.id
        };
    }, [calculateBudgetCapex, budget, gasTariffData, monthlyPerformanceData]);

    /**
     * Handle clicking on a variant row - opens detail modal and fetches additional data if needed
     */
    const handleVariantClick = useCallback(async (variant) => {
        // Open modal immediately with what we have
        setSelectedVariant(variant);
        setDetailModalOpen(true);

        // If variant already has detailed metrics, we're done
        if (variant.annualMetrics && Object.keys(variant.annualMetrics).length > 0) {
            console.log(`📊 [${variant.name}] Already has detail metrics, skipping fetch`);
            return;
        }

        // Need to fetch detail data using the run UUID
        if (!variant.runUuid) {
            console.warn(`📊 [${variant.name}] No run UUID - cannot fetch detail data`);
            return;
        }

        setDetailLoading(true);
        console.log(`📊 [${variant.name}] Fetching detail data for run ${variant.runUuid}...`);

        try {
            // Fetch the solver results using the status endpoint
            const detailToken = await currentUser.getIdToken();
            const statusRes = await fetch(`${API_BASE_URL}/api/site_dna/status/${variant.runUuid}`, {
                headers: {
                    'X-Frontend-Source': 'vortx-flux-site-dna-matrix-builder',
                    'Authorization': `Bearer ${detailToken}`
                }
            });

            if (!statusRes.ok) {
                throw new Error(`Status fetch failed: ${statusRes.status}`);
            }

            const solverData = await statusRes.json();

            // Fetch monthly performance data
            const effectiveTariff = electricTariffData || currentScenarioData?.customTariffData || currentScenarioData?.selectedTariffData;

            if (solverData?.outputs && effectiveTariff) {
                const monthlyData = await monthlyPerformanceService.fetchMonthlyPerformance(
                    solverData,
                    effectiveTariff
                );

                console.log(`📊 [${variant.name}] Monthly data fetched:`, {
                    assetPerformanceMonths: monthlyData?.asset_performance?.length,
                    bauMonths: monthlyData?.bau_monthly_costs?.length
                });

                // Extract metrics from monthly data
                const assetPerformance = monthlyData?.asset_performance || [];
                const annualMetrics = assetPerformance.reduce((acc, month) => ({
                    totalLoad: (acc.totalLoad || 0) + (month.load_kwh || 0),
                    gridImport: (acc.gridImport || 0) + (month.grid_to_load_kwh || 0),
                    solarProduction: (acc.solarProduction || 0) + (month.pv_production_kwh || month.pv_to_load_kwh || 0),
                    batteryThroughput: (acc.batteryThroughput || 0) + (month.storage_to_load_kwh || 0),
                    generatorOutput: (acc.generatorOutput || 0) + (month.generator_to_load_kwh || 0),
                    generatorRuntimeHours: (acc.generatorRuntimeHours || 0) + (month.generator_runtime_hours || 0),
                    fuelUsedMmbtu: (acc.fuelUsedMmbtu || 0) + (month.generator_fuel_used_mmbtu || 0),
                    batteryCycles: (acc.batteryCycles || 0) + (month.battery_cycles || 0),
                    peakLoadKw: Math.max(acc.peakLoadKw || 0, month.peak_load_kw || 0),
                    postDerPeakKw: Math.max(acc.postDerPeakKw || 0, month.post_der_peak_kw || 0),
                }), {});

                const bauMonthlyCosts = monthlyData?.bau_monthly_costs || [];
                const projectMonthlyCosts = monthlyData?.project_monthly_costs || [];
                const annualBauCost = bauMonthlyCosts.reduce((sum, m) => sum + (m.total_cost || 0), 0);
                const annualProjectCost = projectMonthlyCosts.reduce((sum, m) => sum + (m.total_cost || 0), 0);

                // Extract emissions data from solver outputs
                const outputs = solverData?.outputs || {};
                const site = outputs?.Site || {};
                const emissionsBaseline = site.lifecycle_emissions_tonnes_CO2_bau ??
                                           site.annual_emissions_tonnes_CO2_bau ??
                                           outputs?.lifecycle_emissions_tonnes_CO2_bau ?? null;
                const emissionsProject = site.lifecycle_emissions_tonnes_CO2 ??
                                          site.annual_emissions_tonnes_CO2 ??
                                          outputs?.lifecycle_emissions_tonnes_CO2 ?? null;
                const emissionsReduction = (emissionsBaseline !== null && emissionsProject !== null)
                    ? emissionsBaseline - emissionsProject : null;
                const emissionsReductionPct = (emissionsBaseline !== null && emissionsProject !== null && emissionsBaseline > 0)
                    ? ((emissionsBaseline - emissionsProject) / emissionsBaseline) * 100 : null;

                // Update the variant with enriched data
                const enrichedVariant = {
                    ...variant,
                    annualMetrics,
                    annualBauCost,
                    annualProjectCost,
                    annualSavings: annualBauCost - annualProjectCost,
                    monthlyPerformance: assetPerformance,
                    opexAnnual: budget?.opexCalculations?.totalAnnualOM || 0,
                    emissionsBaseline,
                    emissionsProject,
                    emissionsReduction,
                    emissionsReductionPct
                };

                setSelectedVariant(enrichedVariant);

                // Also update the variant in the results list
                setVariantResults(prev => prev.map(v =>
                    v.runUuid === variant.runUuid ? enrichedVariant : v
                ));

                console.log(`📊 [${variant.name}] Detail data enriched successfully`);
            } else {
                console.warn(`📊 [${variant.name}] Cannot fetch detail - missing outputs or tariff`);
            }
        } catch (err) {
            console.error(`📊 [${variant.name}] Failed to fetch detail data:`, err);
        } finally {
            setDetailLoading(false);
        }
    }, [electricTariffData, currentScenarioData, budget]);

    // Set loading to false once we have project context
    useEffect(() => {
        if (projectId && currentUser) {
            setLoading(false);
        }
    }, [projectId, currentUser]);

    // Persist matrix builder state to scenario in Firestore
    useEffect(() => {
        const saveMatrixState = async () => {
            if (!projectId || !currentScenarioId || currentScenarioId === 'new' || !currentUser) return;

            // Only persist matrix CONFIG (not results - those are stored via /variant-runs API)
            const matrixState = {
                pvEnabled, pvValues,
                batteryEnabled, batteryConfigs,
                generatorEnabled, generatorValues
            };

            try {
                const token = await currentUser.getIdToken();
                await fetch(`${API_BASE_URL}/api/site_dna/v1/projects/${projectId}/scenarios/${currentScenarioId}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ matrix_builder_state: matrixState })
                });
            } catch (err) {
                console.error("Failed to save matrix builder state:", err);
            }
        };

        // Debounce save by 1 second
        const timer = setTimeout(saveMatrixState, 1000);
        return () => clearTimeout(timer);
    }, [pvEnabled, pvValues, batteryEnabled, batteryConfigs, generatorEnabled, generatorValues,
        projectId, currentScenarioId, currentUser]);

    // Load matrix builder state from scenario on mount
    useEffect(() => {
        const loadMatrixState = async () => {
            if (!projectId || !currentScenarioId || currentScenarioId === 'new' || !currentUser) return;

            try {
                const token = await currentUser.getIdToken();
                const res = await fetch(`${API_BASE_URL}/api/site_dna/v1/projects/${projectId}/scenarios/${currentScenarioId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (res.ok) {
                    const data = await res.json();
                    const state = data.matrix_builder_state;
                    if (state) {
                        if (state.pvEnabled !== undefined) setPvEnabled(state.pvEnabled);
                        if (state.pvValues?.length) setPvValues(state.pvValues);
                        if (state.batteryEnabled !== undefined) setBatteryEnabled(state.batteryEnabled);
                        if (state.batteryConfigs?.length) setBatteryConfigs(state.batteryConfigs);
                        if (state.generatorEnabled !== undefined) setGeneratorEnabled(state.generatorEnabled);
                        if (state.generatorValues?.length) setGeneratorValues(state.generatorValues);
                    }

                    // Load variant runs from dedicated API endpoint (only if not explicitly cleared)
                    if (!variantsCleared) {
                        const variantsRes = await fetch(`${API_BASE_URL}/api/site_dna/v1/projects/${projectId}/scenarios/${currentScenarioId}/variant-runs`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (variantsRes.ok) {
                            const variantsData = await variantsRes.json();
                            if (variantsData.variants?.length > 0) {
                                // Transform backend format to frontend format
                                const loadedVariants = variantsData.variants.map(v => ({
                                    id: v.id,
                                    name: v.variant_config?.name || 'Unnamed',
                                    pvKw: v.pv_kw || 0,
                                    batteryKwh: v.storage_kwh || 0,
                                    batteryKw: v.storage_kw || 0,
                                    generatorKw: v.generator_kw || 0,
                                    npv: v.npv || 0,
                                    payback: v.simple_payback_years,
                                    yearOneSavings: v.year_one_savings,
                                    annualBauCost: v.annual_bau_cost || 0,
                                    annualProjectCost: v.annual_project_cost || 0,
                                    runUuid: v.nrel_run_uuid,
                                    capex: v.variant_config?.capex || 0,
                                    tariffLabel: v.tariff_label || 'Base Tariff'
                                }));
                                setVariantResults(loadedVariants);
                                console.log(`Loaded ${loadedVariants.length} variant runs from backend`);
                            }
                        }
                    } else {
                        console.log('Skipping variant load - user cleared results');
                    }
                }
            } catch (err) {
                console.error("Failed to load matrix builder state:", err);
            }
        };

        loadMatrixState();
    }, [projectId, currentScenarioId, currentUser, variantsCleared]);

    // ITC Settings Validation
    useEffect(() => {
        if (itcSettings) {
            console.log('[ScenarioAnalysis] ITC Settings received:', {
                includeItc: itcSettings.includeItc,
                hasInputs: !!itcSettings.inputs,
                inputKeys: itcSettings.inputs ? Object.keys(itcSettings.inputs) : []
            });

            // Validate structure
            if (itcSettings.includeItc && !itcSettings.inputs) {
                console.error('[ScenarioAnalysis] INVALID ITC: includeItc=true but inputs missing!');
            }
        } else {
            console.warn('[ScenarioAnalysis] No ITC settings provided - will calculate pre-ITC costs');
        }
    }, [itcSettings]);

    // Generate variants from chip values (matrix of all combinations)
    const generateVariants = useCallback(() => {
        const newVariants = [];

        const pvVals = pvEnabled && pvValues.length > 0 ? pvValues : [0];
        const battConfigs = batteryEnabled && batteryConfigs.length > 0 ? batteryConfigs : [null];
        const genVals = generatorEnabled && generatorValues.length > 0 ? generatorValues : [0];

        // Build tariff list: base tariff + comparison tariffs
        const baseTariff = electricTariffData;
        const baseTariffLabel = baseTariff?.name || baseTariff?.rate_schedule_name || 'Base Tariff';
        const baseTariffWithLabel = { ...baseTariff, _label: baseTariffLabel, _isBase: true };

        const allTariffs = tariffComparisonEnabled && comparisonTariffs.length > 0
            ? [baseTariffWithLabel, ...comparisonTariffs.map(t => ({ ...t, _label: t.name || t.rate_schedule_name }))]
            : [baseTariffWithLabel];

        const showTariffInName = allTariffs.length > 1;

        // Validate: at least one enabled asset with values
        if (!pvEnabled && !batteryEnabled && !generatorEnabled) {
            console.warn('No assets enabled for matrix builder');
            return;
        }

        let id = 1;
        for (const tariff of allTariffs) {
            for (const pv of pvVals) {
                for (const battConfig of battConfigs) {
                    for (const gen of genVals) {
                        const battKw = battConfig?.kW || 0;
                        const battKwh = battConfig ? battConfig.kW * battConfig.duration : 0;

                        // Skip if all zeros
                        if (pv === 0 && battKwh === 0 && gen === 0) continue;

                        // Build name
                        const parts = [];
                        if (pv > 0) parts.push(`PV ${pv}kW`);
                        if (battConfig) parts.push(`${battConfig.kW}kW×${battConfig.duration}h`);
                        if (gen > 0) parts.push(`Gen ${gen}kW`);
                        const assetName = parts.length > 0 ? parts.join(' + ') : 'No DER';

                        const name = showTariffInName
                            ? `${assetName} [${tariff._label}]`
                            : assetName;

                        newVariants.push({
                            id: id++,
                            name,
                            pvKw: pv,
                            batteryKwh: battKwh,
                            batteryKw: battKw,
                            generatorKw: gen,
                            tariff: tariff,
                            tariffLabel: tariff._label,
                            status: 'pending'
                        });
                    }
                }
            }
        }

        setVariants(newVariants);
    }, [pvValues, batteryConfigs, generatorValues, pvEnabled, batteryEnabled, generatorEnabled,
        electricTariffData, tariffComparisonEnabled, comparisonTariffs]);


    // Run all variants in parallel
    const runAllVariants = async () => {
        if (!currentScenarioData?.reopt_inputs) {
            alert('No base scenario data available. Please configure and save your scenario first.');
            return;
        }

        // Validate admin rates are available for cost modeling
        if (!adminCapexRates?.hasValidRates) {
            alert('Cannot run matrix: System CapEx rates not configured. Contact administrator.');
            return;
        }

        setIsRunning(true);
        setRunProgress(0);
        // DON'T reset cleared flag - we only want to show NEW variants
        // Results now accumulate - don't clear previous results

        // Filter out variants that have already been run (duplicate detection)
        // Include tariffLabel in key so same asset config with different tariffs are treated as separate variants
        const alreadyRunKeys = new Set(
            variantResults.map(r => `${r.pvKw}-${r.batteryKw}-${r.batteryKwh}-${r.generatorKw}-${r.tariffLabel || 'base'}`)
        );
        const variantsToRun = variants.filter(v =>
            !alreadyRunKeys.has(`${v.pvKw}-${v.batteryKw}-${v.batteryKwh}-${v.generatorKw}-${v.tariffLabel || 'base'}`)
        );

        if (variantsToRun.length === 0) {
            alert('All variants have already been run. Try different configurations or clear results.');
            setIsRunning(false);
            return;
        }

        try {
            // DEBUG: Log what we have in currentScenarioData.reopt_inputs
            console.log('🔍 [Matrix Builder] currentScenarioData.reopt_inputs available fields:', {
                hasPV: !!currentScenarioData.reopt_inputs?.PV,
                hasElectricStorage: !!currentScenarioData.reopt_inputs?.ElectricStorage,
                hasGenerator: !!currentScenarioData.reopt_inputs?.Generator,
                hasElectricTariff: !!currentScenarioData.reopt_inputs?.ElectricTariff,
                hasElectricLoad: !!currentScenarioData.reopt_inputs?.ElectricLoad,
                hasFinancial: !!currentScenarioData.reopt_inputs?.Financial,
                hasSite: !!currentScenarioData.reopt_inputs?.Site,
                allKeys: Object.keys(currentScenarioData.reopt_inputs || {}),
                // Show ElectricTariff actual structure
                ElectricTariff: currentScenarioData.reopt_inputs?.ElectricTariff ? {
                    keys: Object.keys(currentScenarioData.reopt_inputs.ElectricTariff),
                    hasStructuredRate: !!currentScenarioData.reopt_inputs.ElectricTariff.urdb_response,
                    hasBlendedRate: !!currentScenarioData.reopt_inputs.ElectricTariff.blended_annual_energy_rate,
                    structuredRateSummary: currentScenarioData.reopt_inputs.ElectricTariff.urdb_response ? {
                        hasEnergyRateStructure: !!currentScenarioData.reopt_inputs.ElectricTariff.urdb_response.energyratestructure,
                        numPeriods: currentScenarioData.reopt_inputs.ElectricTariff.urdb_response.energyratestructure?.length
                    } : null
                } : 'MISSING'
            });

            console.log('🔍 [Matrix Builder] Extracting base config from reopt_inputs:', {
                hasPV: !!currentScenarioData.reopt_inputs.PV,
                hasElectricStorage: !!currentScenarioData.reopt_inputs.ElectricStorage,
                hasGenerator: !!currentScenarioData.reopt_inputs.Generator,
                hasCHP: !!currentScenarioData.reopt_inputs.CHP,
                Generator: currentScenarioData.reopt_inputs.Generator,
                CHP: currentScenarioData.reopt_inputs.CHP
            });

            const baseConfig = {
                PV: currentScenarioData.reopt_inputs.PV,
                ElectricStorage: currentScenarioData.reopt_inputs.ElectricStorage,
                Generator: currentScenarioData.reopt_inputs.Generator,
                CHP: currentScenarioData.reopt_inputs.CHP  // Natural gas generators (backend converts to Generator)
            };
            const baseInputs = { ...currentScenarioData.reopt_inputs };
            delete baseInputs.PV;
            delete baseInputs.ElectricStorage;
            delete baseInputs.Generator;
            delete baseInputs.CHP;

            // HYDRATION FIX: Ensure loads_kw is present - check multiple sources
            if (!baseInputs.ElectricLoad) baseInputs.ElectricLoad = {};
            if (!baseInputs.ElectricLoad.loads_kw || baseInputs.ElectricLoad.loads_kw.length === 0) {
                console.warn("⚠️ Base scenario missing loads_kw. Attempting hydration...");

                // Source 1: Current load profile state (most up-to-date)
                if (mainLoadProfile && mainLoadProfile.length === 8760) {
                    baseInputs.ElectricLoad.loads_kw = mainLoadProfile;
                    console.log("✅ Hydrated loads_kw from mainLoadProfile (current state).");
                }
                // Source 2: Saved UI state baseLoads (raw facility load, saved by useLoadProfile serializer)
                // Note: Only baseLoads is persisted to Firestore, not the derived 'loads' field
                else if (currentScenarioData?.ui_state?.loadProfile?.baseLoads?.length === 8760) {
                    baseInputs.ElectricLoad.loads_kw = currentScenarioData.ui_state.loadProfile.baseLoads;
                    console.log("✅ Hydrated loads_kw from ui_state.loadProfile.baseLoads.");
                }
                // No valid source
                else {
                    console.error("❌ Critical: No loads_kw available from any source:", {
                        mainLoadProfileLength: mainLoadProfile?.length,
                        uiStateLoadsLength: currentScenarioData?.ui_state?.loadProfile?.baseLoads?.length
                    });
                    alert("Validation Error: Missing electric load profile. Please reload the scenario or upload a load profile.");
                    setIsRunning(false);
                    return;
                }
            }

            const optimizationEngine = currentScenarioData?.optimization_engine || 'vortx_flux';
            const frontendSource = 'react-frontend';

            const submissionResults = await Promise.allSettled(
                variantsToRun.map(async (variant) => {
                    updateVariantStatus(variant.id, 'submitting');
                    const payload = buildVariantScenario(variant, baseInputs, baseConfig, adminCapexRates);

                    // COMPREHENSIVE PAYLOAD LOGGING FOR DEBUGGING NPV DISCREPANCY
                    console.log(`🔍 [MATRIX VARIANT: ${variant.name}] Complete payload being sent to solver:`, {
                        hasElectricTariff: !!payload.ElectricTariff,
                        hasElectricLoad: !!payload.ElectricLoad,
                        hasFinancial: !!payload.Financial,
                        hasGenerator: !!payload.Generator,
                        hasPV: !!payload.PV,
                        hasElectricStorage: !!payload.ElectricStorage,
                        loadProfileLength: payload.ElectricLoad?.loads_kw?.length,

                        // Complete Financial object
                        Financial: payload.Financial,

                        // Complete Generator object (show all params)
                        Generator: payload.Generator ? {
                            ...payload.Generator,
                            _log_note: 'Full generator config above'
                        } : null,

                        // ElectricTariff (show actual structure)
                        ElectricTariff: payload.ElectricTariff ? {
                            // Show what's actually in the tariff object
                            keys: Object.keys(payload.ElectricTariff),
                            hasStructuredRate: !!payload.ElectricTariff.urdb_response,
                            hasBlendedRate: !!payload.ElectricTariff.blended_annual_energy_rate,
                            hasCustomSeries: !!payload.ElectricTariff.custom_electricity_rate_series_kw,
                            // For structured tariffs, show rate structure summary
                            structuredRateSummary: payload.ElectricTariff.urdb_response ? {
                                hasEnergyRateStructure: !!payload.ElectricTariff.urdb_response.energyratestructure,
                                numEnergyPeriods: payload.ElectricTariff.urdb_response.energyratestructure?.length,
                                hasDemandRateStructure: !!payload.ElectricTariff.urdb_response.demandratestructure,
                                hasSchedules: !!payload.ElectricTariff.urdb_response.energyweekdayschedule
                            } : null
                        } : null,

                        // ElectricLoad (excluding the 8760 array)
                        ElectricLoad: {
                            year: payload.ElectricLoad?.year,
                            critical_load_fraction: payload.ElectricLoad?.critical_load_fraction,
                            loads_kw_length: payload.ElectricLoad?.loads_kw?.length,
                            loads_kw_sum: payload.ElectricLoad?.loads_kw?.reduce((a, b) => a + b, 0)
                        },

                        // PV config
                        PV: payload.PV,

                        // ElectricStorage config
                        ElectricStorage: payload.ElectricStorage,

                        // Site
                        Site: payload.Site
                    });

                    const token = await currentUser.getIdToken();
                    const response = await fetch(`${API_BASE_URL}/api/site_dna/submit`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-Frontend-Source': frontendSource,
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({
                            ...payload,
                            optimization_engine: optimizationEngine
                        })
                    });
                    const data = await response.json();
                    if (!response.ok) {
                        throw new Error(data.error || `Submission failed: ${response.status}`);
                    }
                    updateVariantStatus(variant.id, 'submitted');
                    return { variant, runUuid: data.run_uuid };
                })
            );

            const successfulSubmissions = [];
            submissionResults.forEach((result, index) => {
                const variant = variants[index];
                if (result.status === 'fulfilled') {
                    successfulSubmissions.push(result.value);
                } else {
                    updateVariantStatus(variant.id, 'failed');
                    console.error(`Submission failed for ${variant.name}:`, result.reason);
                }
            });

            let completedCount = 0;

            const pollVariant = async (variant, runUuid) => {
                const maxAttempts = 120;
                let attempts = 0;

                while (attempts < maxAttempts) {
                    attempts += 1;
                    updateVariantStatus(variant.id, 'running');

                    const pollToken = await currentUser.getIdToken();
                    const statusRes = await fetch(`${API_BASE_URL}/api/site_dna/status/${runUuid}`, {
                        headers: {
                            'X-Frontend-Source': frontendSource,
                            'Authorization': `Bearer ${pollToken}`
                        }
                    });
                    const statusData = await statusRes.json();
                    const currentStatus = statusData.status || 'unknown';

                    if (['optimal', 'Optimal Obtained', 'completed'].includes(currentStatus)) {
                        updateVariantStatus(variant.id, 'completed');
                        return statusData;
                    }

                    if (['error', 'Error', 'failed', 'Failed', 'timed_out', 'infeasible'].includes(currentStatus)) {
                        updateVariantStatus(variant.id, 'failed');
                        throw new Error(statusData.error || statusData.messages?.errors?.join(', ') || 'Optimization failed');
                    }

                    await new Promise((resolve) => setTimeout(resolve, 5000));
                }

                updateVariantStatus(variant.id, 'failed');
                throw new Error('Optimization timed out');
            };

            await Promise.all(
                successfulSubmissions.map(async ({ variant, runUuid }) => {
                    try {
                        const data = await pollVariant(variant, runUuid);

                        // Calculate variant-specific monthly performance data
                        // This ensures accurate NPV calculations using the variant's actual system performance
                        let variantMonthlyData = null;
                        try {
                            // Use variant's tariff for monthly performance (not global electricTariffData)
                            // This ensures each variant's BAU vs project costs use the correct tariff
                            const effectiveTariff = variant.tariff || electricTariffData ||
                                currentScenarioData?.customTariffData || currentScenarioData?.selectedTariffData;

                            console.log(`📊 [${variant.name}] TARIFF DEBUG:`, {
                                hasVariantTariff: !!variant.tariff,
                                variantTariffLabel: variant.tariffLabel,
                                hasElectricTariffData: !!electricTariffData,
                                electricTariffDataKeys: electricTariffData ? Object.keys(electricTariffData).slice(0, 10) : null,
                                hasCustomTariffData: !!currentScenarioData?.customTariffData,
                                hasSelectedTariffData: !!currentScenarioData?.selectedTariffData,
                                effectiveTariffExists: !!effectiveTariff,
                                tariffLabel: effectiveTariff?.label || effectiveTariff?.name || 'N/A',
                                // Check solver results for embedded tariff
                                hasResultsInputs: !!data?.inputs,
                                hasResultsElectricTariff: !!data?.inputs?.ElectricTariff,
                                resultsTariffKeys: data?.inputs?.ElectricTariff ? Object.keys(data.inputs.ElectricTariff).slice(0, 10) : null,
                                hasLoadSeries: !!data?.outputs?.ElectricLoad?.load_series_kw,
                                loadSeriesLength: data?.outputs?.ElectricLoad?.load_series_kw?.length
                            });

                            if (data?.outputs) {
                                console.log(`📊 [${variant.name}] Calculating variant-specific monthly performance...`);
                                variantMonthlyData = await monthlyPerformanceService.fetchMonthlyPerformance(
                                    data,  // Variant solver results
                                    effectiveTariff
                                );
                                console.log(`📊 [${variant.name}] Monthly performance calculated:`, {
                                    bauMonths: variantMonthlyData?.bau_monthly_costs?.length,
                                    projectMonths: variantMonthlyData?.project_monthly_costs?.length,
                                    assetPerformanceMonths: variantMonthlyData?.asset_performance?.length,
                                    tariffSource: variantMonthlyData?.tariff_source,
                                    tariffWarning: variantMonthlyData?.tariff_warning,
                                    sampleBauCost: variantMonthlyData?.bau_monthly_costs?.[0]?.total_cost,
                                    sampleProjectCost: variantMonthlyData?.project_monthly_costs?.[0]?.total_cost,
                                    sampleAssetPerformance: variantMonthlyData?.asset_performance?.[0]
                                });
                            } else {
                                console.warn(`⚠️ [${variant.name}] No outputs in solver data - cannot calculate monthly performance`);
                            }
                        } catch (monthlyErr) {
                            console.error(`❌ [${variant.name}] Failed to calculate variant monthly performance:`, monthlyErr);
                            // No fallback — variant must use its own data
                        }

                        const summary = summarizeVariantResults(variant, { ...data, run_uuid: runUuid }, variantMonthlyData);

                        // Add result, checking for duplicates based on configuration + tariff
                        setVariantResults((prev) => {
                            const key = `${summary.pvKw}-${summary.batteryKw}-${summary.batteryKwh}-${summary.generatorKw}-${summary.tariffLabel || 'base'}`;
                            const exists = prev.some(r => `${r.pvKw}-${r.batteryKw}-${r.batteryKwh}-${r.generatorKw}-${r.tariffLabel || 'base'}` === key);
                            return exists ? prev : [...prev, summary];
                        });

                        // Persist variant run to backend (builds optimization dataset)
                        try {
                            const token = await currentUser.getIdToken();
                            await fetch(`${API_BASE_URL}/api/site_dna/v1/projects/${projectId}/scenarios/${currentScenarioId}/variant-runs`, {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${token}`
                                },
                                body: JSON.stringify({
                                    nrel_run_uuid: runUuid,
                                    status: 'completed',
                                    variant_config: {
                                        name: variant.name,
                                        pv_kw: variant.pvKw,
                                        battery_kwh: variant.batteryKwh,
                                        battery_kw: variant.batteryKw,
                                        generator_kw: variant.generatorKw,
                                        capex: summary.capex
                                    },
                                    npv: summary.npv,
                                    simple_payback_years: summary.payback,
                                    year_one_savings: summary.yearOneSavings,
                                    annual_bau_cost: summary.annualBauCost,
                                    annual_project_cost: summary.annualProjectCost,
                                    pv_kw: summary.pvKw,
                                    storage_kw: summary.batteryKw,
                                    storage_kwh: summary.batteryKwh,
                                    generator_kw: summary.generatorKw,
                                    tariff_label: summary.tariffLabel
                                })
                            });
                            console.log(`Saved variant ${variant.name} to backend`);
                        } catch (saveErr) {
                            console.error(`Failed to save variant ${variant.name} to backend:`, saveErr);
                        }
                    } catch (error) {
                        console.error(`Variant ${variant.name} failed:`, error);
                    } finally {
                        completedCount += 1;
                        setRunProgress((completedCount / variantsToRun.length) * 100);
                    }
                })
            );
        } catch (err) {
            console.error('Error running variants:', err);
            alert(`Error: ${err.message}`);
        } finally {
            setIsRunning(false);
            setRunProgress(100);
        }
    };

    // Sort results by NPV (higher = better) or LCC (lower = better) if no NPV
    const sortedResults = useMemo(() => {
        if (!variantResults.length) return [];
        return [...variantResults].sort((a, b) => {
            // If both have real NPV values, sort by NPV (higher is better)
            if (a.npv !== 0 && b.npv !== 0) {
                return sortOrder === 'desc' ? b.npv - a.npv : a.npv - b.npv;
            }
            // Otherwise sort by LCC (lower is better)
            const lccA = a.lcc || Infinity;
            const lccB = b.lcc || Infinity;
            return sortOrder === 'desc' ? lccA - lccB : lccB - lccA;
        });
    }, [variantResults, sortOrder]);

    // Determine if we should show tariff column in results table
    const showTariffColumn = useMemo(() => {
        return sortedResults.length > 1 &&
            sortedResults.some(r => r.tariffLabel !== sortedResults[0]?.tariffLabel);
    }, [sortedResults]);

    // Promote a variant run to a standalone scenario via the backend
    // Copies source scenario config + variant DER sizing + full solver results
    const [promoting, setPromoting] = useState(null); // variant id being promoted
    const saveAsScenario = async (variant) => {
        if (!variant.id && !variant.runUuid) {
            console.error('Cannot promote variant: no id or runUuid');
            return;
        }

        setPromoting(variant.id || variant.runUuid);
        try {
            const token = await currentUser.getIdToken();
            const body = {};
            if (variant.id) {
                body.run_id = variant.id;
            } else {
                body.nrel_run_uuid = variant.runUuid;
            }

            const res = await fetch(
                `${API_BASE_URL}/api/site_dna/v1/projects/${projectId}/scenarios/${currentScenarioId}/promote-variant`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(body)
                }
            );

            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.error || `Failed to promote variant (${res.status})`);
            }

            const newScenario = await res.json();
            // Open the new scenario in a new tab
            window.open(`/projects/${projectId}/scenarios/${newScenario.id}`, '_blank');
        } catch (err) {
            console.error('Failed to promote variant to scenario:', err);
            alert(`Failed to create scenario from variant: ${err.message}`);
        } finally {
            setPromoting(null);
        }
    };

    if (loading) return <LinearProgress />;

    return (
        <Box sx={{ pb: 8 }}>
            {/* DER Matrix Builder */}
            <Paper sx={{ p: 3, mb: 4, borderRadius: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontFamily: vortxTypography.heading, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <GridOn sx={{ color: vortxColors.gold }} /> DER Matrix Builder
                    </Typography>
                    <IconButton onClick={() => setMatrixExpanded(!matrixExpanded)}>
                        {matrixExpanded ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                </Box>

                <Collapse in={matrixExpanded}>
                    <Grid container spacing={3}>
                        {/* PV Sizes */}
                        <Grid item xs={12} md={4}>
                            <Card sx={{
                                height: '100%',
                                bgcolor: pvEnabled ? 'rgba(178, 140, 80, 0.05)' : 'transparent',
                                border: `1px solid ${pvEnabled ? 'rgba(178, 140, 80, 0.3)' : 'rgba(255,255,255,0.1)'}`,
                                transition: 'all 0.3s ease'
                            }}>
                                <CardContent>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={pvEnabled}
                                                onChange={(e) => setPvEnabled(e.target.checked)}
                                                sx={{ '&.Mui-checked': { color: vortxColors.gold } }}
                                            />
                                        }
                                        label={<Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>Solar PV</Typography>}
                                        sx={{ mb: 2 }}
                                    />

                                    <Box sx={{ opacity: pvEnabled ? 1 : 0.4 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                            Select from equipment database
                                        </Typography>
                                        <Autocomplete
                                            size="small"
                                            options={solarEquipment}
                                            getOptionLabel={(option) => `${option.manufacturer} ${option.model} - ${option.wattage_per_panel}W`}
                                            onChange={(e, equipment) => {
                                                if (equipment && pvEnabled) {
                                                    const typicalSizes = [50, 100, 250, 500, 1000, 2000];
                                                    typicalSizes.forEach(size => {
                                                        if (!pvValues.includes(size)) {
                                                            setPvValues(prev => [...prev, size].sort((a, b) => a - b));
                                                        }
                                                    });
                                                }
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    placeholder="Search equipment..."
                                                />
                                            )}
                                            disabled={!pvEnabled}
                                            sx={{ mb: 2 }}
                                        />

                                        <Divider sx={{ my: 2 }}>
                                            <Typography variant="caption" color="text.secondary">OR</Typography>
                                        </Divider>

                                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                            Enter custom size (press Enter)
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            placeholder="Enter kW capacity"
                                            type="number"
                                            size="small"
                                            value={pvInput}
                                            onChange={(e) => setPvInput(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && pvInput && pvEnabled) {
                                                    const val = Number(pvInput);
                                                    if (val > 0 && !pvValues.includes(val)) {
                                                        setPvValues([...pvValues, val].sort((a, b) => a - b));
                                                    }
                                                    setPvInput('');
                                                }
                                            }}
                                            inputProps={{ min: 1 }}
                                            disabled={!pvEnabled}
                                            sx={{ mb: 2 }}
                                        />

                                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                            Selected sizes ({pvValues.length})
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, minHeight: 32, maxHeight: 120, overflowY: 'auto' }}>
                                            {pvValues.map(v => (
                                                <Chip
                                                    key={v}
                                                    label={`${v} kW`}
                                                    size="small"
                                                    onDelete={pvEnabled ? () => setPvValues(pvValues.filter(x => x !== v)) : undefined}
                                                    sx={{
                                                        bgcolor: 'rgba(178, 140, 80, 0.2)',
                                                        color: vortxColors.gold,
                                                        fontWeight: 600
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Battery Sizes - kW + Duration */}
                        <Grid item xs={12} md={4}>
                            <Card sx={{
                                height: '100%',
                                bgcolor: batteryEnabled ? 'rgba(63, 106, 100, 0.05)' : 'transparent',
                                border: `1px solid ${batteryEnabled ? 'rgba(63, 106, 100, 0.3)' : 'rgba(255,255,255,0.1)'}`,
                                transition: 'all 0.3s ease'
                            }}>
                                <CardContent>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={batteryEnabled}
                                                onChange={(e) => setBatteryEnabled(e.target.checked)}
                                                sx={{ '&.Mui-checked': { color: vortxColors.teal } }}
                                            />
                                        }
                                        label={<Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>Battery Storage</Typography>}
                                        sx={{ mb: 2 }}
                                    />

                                    <Box sx={{ opacity: batteryEnabled ? 1 : 0.4 }}>
                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
                                            <Typography variant="caption" color="text.secondary">Duration:</Typography>
                                            <Box sx={{ display: 'flex', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                                <Button
                                                    size="small"
                                                    variant={batteryDuration === 2 ? 'contained' : 'text'}
                                                    onClick={() => setBatteryDuration(2)}
                                                    disabled={!batteryEnabled}
                                                    sx={{
                                                        minWidth: 50,
                                                        bgcolor: batteryDuration === 2 ? vortxColors.teal : 'transparent',
                                                        '&:hover': { bgcolor: batteryDuration === 2 ? vortxColors.teal : 'action.hover' }
                                                    }}
                                                >
                                                    2h
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant={batteryDuration === 4 ? 'contained' : 'text'}
                                                    onClick={() => setBatteryDuration(4)}
                                                    disabled={!batteryEnabled}
                                                    sx={{
                                                        minWidth: 50,
                                                        bgcolor: batteryDuration === 4 ? vortxColors.teal : 'transparent',
                                                        '&:hover': { bgcolor: batteryDuration === 4 ? vortxColors.teal : 'action.hover' }
                                                    }}
                                                >
                                                    4h
                                                </Button>
                                            </Box>
                                        </Box>

                                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                            Select from equipment database
                                        </Typography>
                                        <Autocomplete
                                            size="small"
                                            options={batteryEquipment}
                                            getOptionLabel={(option) => `${option.manufacturer} ${option.model} - ${option.power_kw}kW / ${option.capacity_kwh}kWh`}
                                            onChange={(e, equipment) => {
                                                if (equipment && batteryEnabled) {
                                                    const kW = equipment.power_kw;
                                                    const exists = batteryConfigs.some(c => c.kW === kW && c.duration === batteryDuration);
                                                    if (!exists) {
                                                        setBatteryConfigs([...batteryConfigs, { kW, duration: batteryDuration }]
                                                            .sort((a, b) => (a.kW * a.duration) - (b.kW * b.duration)));
                                                    }
                                                }
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    placeholder="Search equipment..."
                                                />
                                            )}
                                            disabled={!batteryEnabled}
                                            sx={{ mb: 2 }}
                                        />

                                        <Divider sx={{ my: 2 }}>
                                            <Typography variant="caption" color="text.secondary">OR</Typography>
                                        </Divider>

                                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                            Enter custom power (press Enter)
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            placeholder="Enter kW power"
                                            type="number"
                                            size="small"
                                            value={batteryInput}
                                            onChange={(e) => setBatteryInput(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && batteryInput && batteryEnabled) {
                                                    const kW = Number(batteryInput);
                                                    const exists = batteryConfigs.some(c => c.kW === kW && c.duration === batteryDuration);
                                                    if (kW > 0 && !exists) {
                                                        setBatteryConfigs([...batteryConfigs, { kW, duration: batteryDuration }]
                                                            .sort((a, b) => (a.kW * a.duration) - (b.kW * b.duration)));
                                                    }
                                                    setBatteryInput('');
                                                }
                                            }}
                                            inputProps={{ min: 1 }}
                                            disabled={!batteryEnabled}
                                            sx={{ mb: 2 }}
                                        />

                                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                            Selected configurations ({batteryConfigs.length})
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, minHeight: 32, maxHeight: 120, overflowY: 'auto' }}>
                                            {batteryConfigs.map((config, idx) => (
                                                <Chip
                                                    key={`${config.kW}-${config.duration}`}
                                                    label={`${config.kW}kW×${config.duration}h`}
                                                    size="small"
                                                    onDelete={batteryEnabled ? () => setBatteryConfigs(
                                                        batteryConfigs.filter((_, i) => i !== idx)
                                                    ) : undefined}
                                                    sx={{
                                                        bgcolor: 'rgba(63, 106, 100, 0.2)',
                                                        color: vortxColors.teal,
                                                        fontWeight: 600
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Generator Sizes */}
                        <Grid item xs={12} md={4}>
                            <Card sx={{
                                height: '100%',
                                bgcolor: generatorEnabled ? 'rgba(255, 107, 53, 0.05)' : 'transparent',
                                border: `1px solid ${generatorEnabled ? 'rgba(255, 107, 53, 0.3)' : 'rgba(255,255,255,0.1)'}`,
                                transition: 'all 0.3s ease'
                            }}>
                                <CardContent>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={generatorEnabled}
                                                onChange={(e) => setGeneratorEnabled(e.target.checked)}
                                                sx={{ '&.Mui-checked': { color: '#FF6B35' } }}
                                            />
                                        }
                                        label={<Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>Generator</Typography>}
                                        sx={{ mb: 2 }}
                                    />

                                    <Box sx={{ opacity: generatorEnabled ? 1 : 0.4 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                            Select from equipment database
                                        </Typography>
                                        <Autocomplete
                                            size="small"
                                            options={generatorEquipment}
                                            getOptionLabel={(option) => `${option.manufacturer} ${option.model} - ${option.power_kw}kW (${option.fuel_type})`}
                                            onChange={(e, equipment) => {
                                                if (equipment && generatorEnabled) {
                                                    const kW = equipment.power_kw;
                                                    if (!generatorValues.includes(kW)) {
                                                        setGeneratorValues([...generatorValues, kW].sort((a, b) => a - b));
                                                    }
                                                }
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    placeholder="Search equipment..."
                                                />
                                            )}
                                            disabled={!generatorEnabled}
                                            sx={{ mb: 2 }}
                                        />

                                        <Divider sx={{ my: 2 }}>
                                            <Typography variant="caption" color="text.secondary">OR</Typography>
                                        </Divider>

                                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                            Enter custom size (press Enter)
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            placeholder="Enter kW capacity"
                                            type="number"
                                            size="small"
                                            value={generatorInput}
                                            onChange={(e) => setGeneratorInput(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && generatorInput && generatorEnabled) {
                                                    const val = Number(generatorInput);
                                                    if (val > 0 && !generatorValues.includes(val)) {
                                                        setGeneratorValues([...generatorValues, val].sort((a, b) => a - b));
                                                    }
                                                    setGeneratorInput('');
                                                }
                                            }}
                                            inputProps={{ min: 1 }}
                                            disabled={!generatorEnabled}
                                            sx={{ mb: 2 }}
                                        />

                                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                            Selected sizes ({generatorValues.length})
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, minHeight: 32, maxHeight: 120, overflowY: 'auto' }}>
                                            {generatorValues.map(v => (
                                                <Chip
                                                    key={v}
                                                    label={`${v} kW`}
                                                    size="small"
                                                    onDelete={generatorEnabled ? () => setGeneratorValues(generatorValues.filter(x => x !== v)) : undefined}
                                                    sx={{
                                                        bgcolor: 'rgba(255, 107, 53, 0.2)',
                                                        color: '#FF6B35',
                                                        fontWeight: 600
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Tariff Comparison Section */}
                        <Grid item xs={12}>
                            <Card sx={{
                                height: '100%',
                                bgcolor: tariffComparisonEnabled ? 'rgba(178, 140, 80, 0.05)' : 'transparent',
                                border: `1px solid ${tariffComparisonEnabled ? 'rgba(178, 140, 80, 0.3)' : 'rgba(255,255,255,0.1)'}`,
                                transition: 'all 0.2s ease',
                            }}>
                                <CardContent>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={tariffComparisonEnabled}
                                                onChange={(e) => setTariffComparisonEnabled(e.target.checked)}
                                                sx={{ '&.Mui-checked': { color: vortxColors.gold } }}
                                            />
                                        }
                                        label={
                                            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                                                Compare Tariffs
                                            </Typography>
                                        }
                                    />

                                    <Box sx={{ mt: 1, opacity: tariffComparisonEnabled ? 1 : 0.5, transition: 'opacity 0.2s' }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                            Base tariff (always included):
                                        </Typography>
                                        <Chip
                                            label={electricTariffData?.name || electricTariffData?.rate_schedule_name || 'Base Scenario Tariff'}
                                            size="small"
                                            sx={{ mb: 2, bgcolor: 'rgba(178, 140, 80, 0.3)', fontWeight: 600 }}
                                        />

                                        {tariffComparisonEnabled && (
                                            <>
                                                <Divider sx={{ my: 2 }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Add comparison tariffs
                                                    </Typography>
                                                </Divider>

                                                <ApprovedTariffSelector
                                                    currentTariffId={null}
                                                    onTariffSelect={(rate) => {
                                                        // Add to comparison list if not already present
                                                        const isDuplicate = comparisonTariffs.some(t => t.id === rate.id);
                                                        // Check multiple identifiers to catch base tariff in different formats
                                                        const baseTariffName = electricTariffData?.name || electricTariffData?.rate_schedule_name;
                                                        const isBaseTariff = rate.id === electricTariffData?.id ||
                                                            (rate.name && baseTariffName && rate.name === baseTariffName);
                                                        if (!isDuplicate && !isBaseTariff) {
                                                            setComparisonTariffs([...comparisonTariffs, rate]);
                                                        }
                                                    }}
                                                />

                                                {comparisonTariffs.length > 0 && (
                                                    <>
                                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                                                            Additional tariffs ({comparisonTariffs.length}):
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                                            {comparisonTariffs.map(t => (
                                                                <Chip
                                                                    key={t.id}
                                                                    label={t.name || t.rate_schedule_name}
                                                                    size="small"
                                                                    onDelete={() => setComparisonTariffs(
                                                                        comparisonTariffs.filter(x => x.id !== t.id)
                                                                    )}
                                                                    sx={{
                                                                        bgcolor: 'rgba(63, 106, 100, 0.2)',
                                                                        color: vortxColors.teal,
                                                                        '& .MuiChip-deleteIcon': { color: vortxColors.teal }
                                                                    }}
                                                                />
                                                            ))}
                                                        </Box>
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Matrix Summary & Run Button */}
                    <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            <strong>Matrix:</strong>{' '}
                            {pvEnabled && pvValues.length > 0 ? `${pvValues.length} PV` : 'No PV'}{' × '}
                            {batteryEnabled && batteryConfigs.length > 0 ? `${batteryConfigs.length} Battery` : 'No Battery'}{' × '}
                            {generatorEnabled && generatorValues.length > 0 ? `${generatorValues.length} Gen` : 'No Gen'}
                            {tariffComparisonEnabled && comparisonTariffs.length > 0 && (
                                <>{' × '}<strong style={{ color: vortxColors.gold }}>{comparisonTariffs.length + 1} Tariffs</strong></>
                            )}
                            {(pvEnabled || batteryEnabled || generatorEnabled) && (
                                <> = <strong>
                                    {Math.max(1,
                                        (pvEnabled && pvValues.length > 0 ? pvValues.length : 1) *
                                        (batteryEnabled && batteryConfigs.length > 0 ? batteryConfigs.length : 1) *
                                        (generatorEnabled && generatorValues.length > 0 ? generatorValues.length : 1) *
                                        (tariffComparisonEnabled && comparisonTariffs.length > 0 ? comparisonTariffs.length + 1 : 1)
                                    )} combinations
                                </strong></>
                            )}
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <Button
                                variant="outlined"
                                startIcon={<Add />}
                                onClick={generateVariants}
                            >
                                Generate Variants
                            </Button>

                            {variants.length > 0 && (
                                <Button
                                    variant="contained"
                                    startIcon={isRunning ? <CircularProgress size={20} color="inherit" /> : <PlayArrow />}
                                    onClick={runAllVariants}
                                    disabled={isRunning || !hasAdminRates}
                                    sx={{ bgcolor: vortxColors.teal }}
                                >
                                    {!hasAdminRates ? 'Loading rates...' : isRunning ? `Running... ${Math.round(runProgress)}%` : `Run ${variants.length} Variants`}
                                </Button>
                            )}

                            {variantResults.length > 0 && (
                                <Button
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    startIcon={<Delete />}
                                    onClick={async () => {
                                        // Clear from frontend immediately
                                        setVariantResults([]);
                                        setVariantsCleared(true);
                                        console.log('✅ Cleared variants from frontend view');

                                        // Delete from backend so they don't come back
                                        try {
                                            const token = await currentUser.getIdToken();
                                            const response = await fetch(
                                                `${API_BASE_URL}/api/site_dna/v1/projects/${projectId}/scenarios/${currentScenarioId}/variant-runs`,
                                                {
                                                    method: 'DELETE',
                                                    headers: { 'Authorization': `Bearer ${token}` }
                                                }
                                            );
                                            if (response.ok) {
                                                console.log('✅ Deleted all variants from backend');
                                            } else {
                                                console.warn('⚠️ Failed to delete variants from backend:', response.status);
                                            }
                                        } catch (err) {
                                            console.error('Failed to delete variants from backend:', err);
                                        }
                                    }}
                                    disabled={isRunning}
                                >
                                    Clear {variantResults.length} Results
                                </Button>
                            )}
                        </Box>
                    </Box>

                    {/* Variants Preview */}
                    {variants.length > 0 && (
                        <Box sx={{ mt: 3 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                Generated Variants Preview
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {variants.slice(0, 15).map(v => (
                                    <Chip
                                        key={v.id}
                                        label={v.name}
                                        size="small"
                                        variant="outlined"
                                        onDelete={() => setVariants(variants.filter(x => x.id !== v.id))}
                                    />
                                ))}
                                {variants.length > 15 && (
                                    <Chip label={`+${variants.length - 15} more`} size="small" />
                                )}
                            </Box>
                        </Box>
                    )}
                </Collapse>
            </Paper>

            {/* Results Table (when available) */}
            {sortedResults.length > 0 && (
                <Paper sx={{ p: 3, mb: 4, borderRadius: 2, bgcolor: 'background.paper' }}>
                    <Typography variant="h6" sx={{ fontFamily: vortxTypography.heading, mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: 'text.primary' }}>
                        <EmojiEvents sx={{ color: vortxColors.gold }} /> Analysis Results (Ranked by NPV)
                    </Typography>

                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700, color: vortxColors.gold }}>#</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: vortxColors.gold }}>Configuration</TableCell>
                                    {showTariffColumn && (
                                        <TableCell sx={{ fontWeight: 700, color: vortxColors.gold }}>Tariff</TableCell>
                                    )}
                                    <TableCell align="right" sx={{ fontWeight: 700, color: vortxColors.gold }}>PV (kW)</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700, color: vortxColors.gold }}>Battery (kWh)</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700, color: vortxColors.gold }}>BAU Utility ($/yr)</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700, color: vortxColors.gold }}>Project Utility ($/yr)</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700, color: vortxColors.gold }}>
                                        <TableSortLabel
                                            active
                                            direction={sortOrder}
                                            onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                                        >
                                            NPV ($)
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700, color: vortxColors.gold }}>Capex ($)</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700, color: vortxColors.gold }}>Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sortedResults.map((result, idx) => (
                                    <TableRow
                                        key={`${result.name}-${result.pvKw}-${result.batteryKw}-${result.batteryKwh}-${result.generatorKw}-${result.runUuid || idx}`}
                                        onClick={() => handleVariantClick(result)}
                                        sx={{
                                            cursor: 'pointer',
                                            bgcolor: idx === 0 && sortOrder === 'desc' ?
                                                'rgba(255, 184, 0, 0.1)' : 'inherit',
                                            '&:hover': { bgcolor: 'action.hover' }
                                        }}
                                    >
                                        <TableCell>
                                            {idx === 0 && sortOrder === 'desc' && (
                                                <EmojiEvents sx={{ color: vortxColors.gold, fontSize: 18 }} />
                                            )}
                                            {idx + 1}
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: idx === 0 ? 600 : 400 }}>
                                            {result.name}
                                        </TableCell>
                                        {showTariffColumn && (
                                            <TableCell>
                                                <Chip
                                                    label={result.tariffLabel}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{
                                                        fontSize: '0.7rem',
                                                        borderColor: result.tariffLabel === (electricTariffData?.name || electricTariffData?.rate_schedule_name || 'Base Tariff')
                                                            ? vortxColors.gold
                                                            : vortxColors.teal
                                                    }}
                                                />
                                            </TableCell>
                                        )}
                                        <TableCell align="right">{result.pvKw?.toLocaleString() || '-'}</TableCell>
                                        <TableCell align="right">{result.batteryKwh?.toLocaleString() || '-'}</TableCell>
                                        <TableCell align="right" sx={{ color: 'text.secondary' }}>
                                            ${result.annualBauCost?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '-'}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 500 }}>
                                            ${result.annualProjectCost?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '-'}
                                        </TableCell>
                                        <TableCell align="right" sx={{
                                            fontWeight: 600,
                                            color: result.npv > 0 ? 'success.main' : 'error.main'
                                        }}>
                                            ${result.npv?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '-'}
                                        </TableCell>
                                        <TableCell align="right">
                                            ${result.capex?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '-'}
                                            {result.usingBudgetRates && (
                                                <Chip
                                                    label="Budget"
                                                    size="small"
                                                    sx={{
                                                        ml: 0.5,
                                                        height: 16,
                                                        fontSize: '0.65rem',
                                                        bgcolor: 'rgba(63, 106, 100, 0.2)',
                                                        color: vortxColors.teal,
                                                        '& .MuiChip-label': { px: 0.75 }
                                                    }}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                                            <Tooltip title="Save as New Scenario">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => saveAsScenario(result)}
                                                    disabled={promoting === (result.id || result.runUuid)}
                                                    sx={{ color: vortxColors.teal }}
                                                >
                                                    {promoting === (result.id || result.runUuid)
                                                        ? <CircularProgress size={16} />
                                                        : <OpenInNew fontSize="small" />
                                                    }
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete this variant">
                                                <IconButton
                                                    size="small"
                                                    onClick={async (e) => {
                                                        e.stopPropagation();
                                                        // Remove from frontend state
                                                        setVariantResults(prev => prev.filter(r => r !== result));
                                                        // Delete from backend using Firestore doc ID
                                                        const deleteId = result.id || result.runUuid;
                                                        if (deleteId) {
                                                            try {
                                                                const token = await currentUser.getIdToken();
                                                                await fetch(
                                                                    `${API_BASE_URL}/api/site_dna/v1/projects/${projectId}/scenarios/${currentScenarioId}/variant-runs/${deleteId}`,
                                                                    {
                                                                        method: 'DELETE',
                                                                        headers: { 'Authorization': `Bearer ${token}` }
                                                                    }
                                                                );
                                                            } catch (err) {
                                                                console.error('Failed to delete variant from backend:', err);
                                                            }
                                                        }
                                                    }}
                                                    sx={{ color: 'error.main', opacity: 0.6, '&:hover': { opacity: 1 } }}
                                                >
                                                    <Close fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            {/* Variant Detail Modal */}
            <Dialog
                open={detailModalOpen}
                onClose={() => setDetailModalOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'rgba(31, 46, 43, 0.15)',
                    borderBottom: `2px solid ${vortxColors.gold}`
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Analytics sx={{ color: vortxColors.gold }} />
                        <Typography variant="h6" sx={{ fontFamily: vortxTypography.heading, color: 'text.primary' }}>
                            {selectedVariant?.name} - Detailed Analysis
                        </Typography>
                    </Box>
                    <IconButton onClick={() => setDetailModalOpen(false)} size="small">
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    {detailLoading && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, p: 2, bgcolor: 'rgba(63, 106, 100, 0.1)', borderRadius: 1 }}>
                            <CircularProgress size={20} />
                            <Typography color="text.secondary">Loading detailed performance data...</Typography>
                        </Box>
                    )}
                    {selectedVariant && (
                        <Grid container spacing={3}>
                            {/* Financial Summary */}
                            <Grid item xs={12}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: vortxColors.gold }}>
                                    <AttachMoney sx={{ fontSize: 20, mr: 0.5, verticalAlign: 'middle' }} />
                                    Financial Summary
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={6} sm={3}>
                                        <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'rgba(63, 106, 100, 0.1)' }}>
                                            <Typography variant="caption" color="text.secondary">NPV (20 yr)</Typography>
                                            <Typography variant="h6" sx={{ color: selectedVariant.npv > 0 ? 'success.main' : 'error.main', fontWeight: 700 }}>
                                                ${selectedVariant.npv?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '-'}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'rgba(63, 106, 100, 0.1)' }}>
                                            <Typography variant="caption" color="text.secondary">Payback</Typography>
                                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                                {selectedVariant.payback?.toFixed(1) || '-'} yrs
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'rgba(63, 106, 100, 0.1)' }}>
                                            <Typography variant="caption" color="text.secondary">CapEx</Typography>
                                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                                ${selectedVariant.capex?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '-'}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'rgba(63, 106, 100, 0.1)' }}>
                                            <Typography variant="caption" color="text.secondary">Annual O&M</Typography>
                                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                                ${selectedVariant.opexAnnual?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '-'}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                </Grid>
                            </Grid>

                            {/* Annual Cost Comparison */}
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: vortxColors.gold }}>
                                    Annual Utility Cost Comparison
                                </Typography>
                                <Paper sx={{ p: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography color="text.secondary">BAU (No System)</Typography>
                                        <Typography sx={{ fontWeight: 600 }}>
                                            ${selectedVariant.annualBauCost?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '-'}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography color="text.secondary">With System</Typography>
                                        <Typography sx={{ fontWeight: 600 }}>
                                            ${selectedVariant.annualProjectCost?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '-'}
                                        </Typography>
                                    </Box>
                                    <Divider sx={{ my: 1 }} />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography sx={{ fontWeight: 600, color: 'success.main' }}>Annual Savings</Typography>
                                        <Typography sx={{ fontWeight: 700, color: 'success.main' }}>
                                            ${selectedVariant.annualSavings?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '-'}
                                        </Typography>
                                    </Box>
                                </Paper>
                            </Grid>

                            {/* System Configuration */}
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: vortxColors.gold }}>
                                    System Configuration
                                </Typography>
                                <Paper sx={{ p: 2 }}>
                                    {selectedVariant.pvKw > 0 && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <WbSunny sx={{ color: vortxColors.gold, fontSize: 20 }} />
                                                <Typography>Solar PV</Typography>
                                            </Box>
                                            <Typography sx={{ fontWeight: 600 }}>{selectedVariant.pvKw?.toLocaleString()} kW</Typography>
                                        </Box>
                                    )}
                                    {selectedVariant.batteryKwh > 0 && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <BatteryChargingFull sx={{ color: vortxColors.teal, fontSize: 20 }} />
                                                <Typography>Battery Storage</Typography>
                                            </Box>
                                            <Typography sx={{ fontWeight: 600 }}>
                                                {selectedVariant.batteryKw?.toLocaleString()} kW / {selectedVariant.batteryKwh?.toLocaleString()} kWh
                                            </Typography>
                                        </Box>
                                    )}
                                    {selectedVariant.generatorKw > 0 && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <LocalGasStation sx={{ color: '#ff7c7c', fontSize: 20 }} />
                                                <Typography>Generator</Typography>
                                            </Box>
                                            <Typography sx={{ fontWeight: 600 }}>{selectedVariant.generatorKw?.toLocaleString()} kW</Typography>
                                        </Box>
                                    )}
                                </Paper>
                            </Grid>

                            {/* Performance Metrics */}
                            <Grid item xs={12}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: vortxColors.gold }}>
                                    <Speed sx={{ fontSize: 20, mr: 0.5, verticalAlign: 'middle' }} />
                                    Annual Performance Metrics
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={6} sm={4} md={3}>
                                        <Paper sx={{ p: 2 }}>
                                            <Typography variant="caption" color="text.secondary">Total Load</Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                                {selectedVariant.annualMetrics?.totalLoad?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '-'} kWh
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={6} sm={4} md={3}>
                                        <Paper sx={{ p: 2 }}>
                                            <Typography variant="caption" color="text.secondary">Grid Import</Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                                {selectedVariant.annualMetrics?.gridImport?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '-'} kWh
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    {selectedVariant.pvKw > 0 && (
                                        <Grid item xs={6} sm={4} md={3}>
                                            <Paper sx={{ p: 2 }}>
                                                <Typography variant="caption" color="text.secondary">Solar Production</Typography>
                                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                                    {selectedVariant.annualMetrics?.solarProduction?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '-'} kWh
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                    )}
                                    {selectedVariant.batteryKwh > 0 && (
                                        <>
                                            <Grid item xs={6} sm={4} md={3}>
                                                <Paper sx={{ p: 2 }}>
                                                    <Typography variant="caption" color="text.secondary">Battery Throughput</Typography>
                                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                                        {selectedVariant.annualMetrics?.batteryThroughput?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '-'} kWh
                                                    </Typography>
                                                </Paper>
                                            </Grid>
                                            <Grid item xs={6} sm={4} md={3}>
                                                <Paper sx={{ p: 2 }}>
                                                    <Typography variant="caption" color="text.secondary">Battery Cycles</Typography>
                                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                                        {selectedVariant.annualMetrics?.batteryCycles?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '-'}
                                                    </Typography>
                                                </Paper>
                                            </Grid>
                                        </>
                                    )}
                                    {selectedVariant.generatorKw > 0 && (
                                        <>
                                            <Grid item xs={6} sm={4} md={3}>
                                                <Paper sx={{ p: 2, bgcolor: 'rgba(255, 124, 124, 0.1)' }}>
                                                    <Typography variant="caption" color="text.secondary">Generator Output</Typography>
                                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                                        {selectedVariant.annualMetrics?.generatorOutput?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '-'} kWh
                                                    </Typography>
                                                </Paper>
                                            </Grid>
                                            <Grid item xs={6} sm={4} md={3}>
                                                <Paper sx={{ p: 2, bgcolor: 'rgba(255, 124, 124, 0.1)' }}>
                                                    <Typography variant="caption" color="text.secondary">Generator Runtime</Typography>
                                                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#d32f2f' }}>
                                                        {selectedVariant.annualMetrics?.generatorRuntimeHours?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '-'} hours
                                                    </Typography>
                                                </Paper>
                                            </Grid>
                                            <Grid item xs={6} sm={4} md={3}>
                                                <Paper sx={{ p: 2, bgcolor: 'rgba(255, 124, 124, 0.1)' }}>
                                                    <Typography variant="caption" color="text.secondary">Fuel Used</Typography>
                                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                                        {selectedVariant.annualMetrics?.fuelUsedMmbtu?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '-'} MMBtu
                                                    </Typography>
                                                </Paper>
                                            </Grid>
                                        </>
                                    )}
                                    <Grid item xs={6} sm={4} md={3}>
                                        <Paper sx={{ p: 2 }}>
                                            <Typography variant="caption" color="text.secondary">Peak Demand (BAU)</Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                                {selectedVariant.annualMetrics?.peakLoadKw?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '-'} kW
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={6} sm={4} md={3}>
                                        <Paper sx={{ p: 2 }}>
                                            <Typography variant="caption" color="text.secondary">Peak Demand (w/ DER)</Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                                {selectedVariant.annualMetrics?.postDerPeakKw?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '-'} kW
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                </Grid>
                            </Grid>

                            {/* Emissions Section */}
                            {(selectedVariant.emissionsReduction !== null || selectedVariant.emissionsBaseline !== null) && (
                                <Grid item xs={12}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: vortxColors.gold }}>
                                        <Box component="span" sx={{ mr: 0.5 }}>🌱</Box>
                                        Emissions Impact
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6} sm={3}>
                                            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'rgba(63, 106, 100, 0.1)' }}>
                                                <Typography variant="caption" color="text.secondary">Baseline CO₂</Typography>
                                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                                    {selectedVariant.emissionsBaseline?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '-'} tonnes
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={6} sm={3}>
                                            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'rgba(63, 106, 100, 0.1)' }}>
                                                <Typography variant="caption" color="text.secondary">With System</Typography>
                                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                                    {selectedVariant.emissionsProject?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '-'} tonnes
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={6} sm={3}>
                                            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'rgba(46, 125, 50, 0.15)' }}>
                                                <Typography variant="caption" color="text.secondary">CO₂ Avoided</Typography>
                                                <Typography variant="body1" sx={{ fontWeight: 700, color: 'success.main' }}>
                                                    {selectedVariant.emissionsReduction?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '-'} tonnes
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={6} sm={3}>
                                            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'rgba(46, 125, 50, 0.15)' }}>
                                                <Typography variant="caption" color="text.secondary">Reduction</Typography>
                                                <Typography variant="body1" sx={{ fontWeight: 700, color: 'success.main' }}>
                                                    {selectedVariant.emissionsReductionPct?.toFixed(1) || '-'}%
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )}
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={() => setDetailModalOpen(false)}
                    >
                        Close
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={promoting ? <CircularProgress size={16} color="inherit" /> : <OpenInNew />}
                        disabled={!!promoting}
                        onClick={async () => {
                            if (selectedVariant) {
                                await saveAsScenario(selectedVariant);
                                setDetailModalOpen(false);
                            }
                        }}
                        sx={{ bgcolor: vortxColors.teal }}
                    >
                        {promoting ? 'Creating...' : 'Save as New Scenario'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
